import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { rentalAPI } from '../../utils/api';
import { formatAvailabilityDate } from '../../utils/formatters';
import { rentalDetailPath } from '../../utils/listingPaths';

function toNumberPrice(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getRentalStartingPrice(rental) {
  if (!rental) return null;
  const prices = [];
  const type = rental.rental_type;
  const pushIfListed = (item) => {
    if (item?.isAvailable === false) return;
    const p = toNumberPrice(item?.price);
    if (p != null) prices.push(p);
  };
  if (type === 'entire' && Array.isArray(rental.units)) {
    rental.units.forEach(pushIfListed);
  } else if (type === 'room' && Array.isArray(rental.rooms)) {
    rental.rooms.forEach(pushIfListed);
  } else if (type === 'bed' && Array.isArray(rental.beds)) {
    rental.beds.forEach(pushIfListed);
  }
  if (prices.length > 0) return Math.min(...prices);
  return toNumberPrice(rental.price);
}

function shufflePick(arr, n) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

/** 价格、链接等强调色：深棕，避免紫色 */
const listingEmphasisText = '#3d2914';

function FeaturedHomeListingCard({ rental }) {
  const navigate = useNavigate();
  const img =
    rental.images?.[0] ||
    'https://source.unsplash.com/random/800x600/?apartment';
  const address = [
    rental.address_line1,
    rental.city,
    rental.state,
  ]
    .filter(Boolean)
    .join(', ');
  const startPrice = getRentalStartingPrice(rental);
  const priceLabel =
    startPrice != null ? `$${Math.round(startPrice)}/mo` : '—';
  const amenityChip =
    Array.isArray(rental.amenities) && rental.amenities.length > 0
      ? rental.amenities[0]
      : null;

  const goDetail = useCallback(() => {
    navigate(rentalDetailPath(rental));
  }, [navigate, rental]);

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={goDetail}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia component="img" height="200" image={img} alt={rental.title} />
        {amenityChip && (
          <Chip
            label={amenityChip}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              bgcolor: 'rgba(255,255,255,0.95)',
              fontWeight: 600,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Chip
          label="Available"
          size="small"
          color="success"
          sx={{ position: 'absolute', bottom: 10, left: 10, fontWeight: 600 }}
          onClick={(e) => e.stopPropagation()}
        />
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.92)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label="favorite"
        >
          <FavoriteBorderIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </IconButton>
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {rental.title}
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: listingEmphasisText, flexShrink: 0 }}
          >
            {priceLabel}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {address || 'Los Angeles, CA'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2, color: 'text.secondary' }}>
          <CalendarTodayIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2">
            Available {formatAvailabilityDate(rental.available_date)}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 'auto',
            bgcolor: '#1a1a2e',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.25,
            '&:hover': { bgcolor: '#16213e' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            goDetail();
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}

export default function FeaturedListingsSection() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    rentalAPI
      .getRentals({})
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setFeatured(shufflePick(data, 3));
      })
      .catch(() => {
        if (!cancelled) setFeatured([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loaded && featured.length === 0) {
    return null;
  }

  if (!loaded) {
    return (
      <Container maxWidth="lg" sx={{ mb: 5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Featured Listings
        </Typography>
        <Typography color="text.secondary">Loading featured homes…</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
            Featured Listings
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
            Hand-picked properties perfect for international students, close to universities and transit.
          </Typography>
        </Box>
        <Button
          onClick={() => navigate('/rentals')}
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: 11 }} />}
          sx={{
            alignSelf: { xs: 'flex-start', sm: 'center' },
            color: listingEmphasisText,
            fontWeight: 600,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: 'rgba(61, 41, 20, 0.08)' },
          }}
        >
          View all listings
        </Button>
      </Stack>
      <Grid container spacing={3}>
        {featured.map((rental) => (
          <Grid item xs={12} sm={6} md={4} key={rental.id}>
            <FeaturedHomeListingCard rental={rental} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
